// src/context/MedicalCenterContext.jsx
import React, {useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation, matchPath } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { medicalCenterService } from '../services/medicalCenterService';
import { createBaseContext } from '../hooks/useBaseContext';


const { Context: MedicalCenterContext, useContext: useMedicalCenter } = createBaseContext('MedicalCenter');

export const MedicalCenterProvider = ({ children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [availableMedicalCenters, setAvailableMedicalCenters] = useState([]);
  const [selected, setSelected] = useState(null);

  // Load centers once (per login)
  useEffect(() => {
    const loadCenters = async () => {
      if (!user) { setAvailableMedicalCenters([]); return; }
      const centers = await medicalCenterService.getMedicalCenters();
      setAvailableMedicalCenters((centers || []).map(c => ({
        id: c.id,
        name: c.name,
        address: c.address,
        time_zone: c.time_zone,
        preferred_language: c.preferred_language,
        programs: c.programs || [],
      })));
    };
    loadCenters();
  }, [user]);

  // URL sync using matchPath, while provider stays top-level
  useEffect(() => {
    const match =
      matchPath('/medical_centers/:medicalCenterId/*', location.pathname) ||
      matchPath('/medical_centers/:medicalCenterId', location.pathname);

    const paramMcId = match?.params?.medicalCenterId;
    if (!paramMcId) return;

    const found = availableMedicalCenters.find(mc => mc.id === paramMcId);
    if (found) setSelected(found);
    else if (availableMedicalCenters.length > 0) navigate('/medical_centers', { replace: true });
  }, [location.pathname, availableMedicalCenters, navigate]);

  const selectMedicalCenter = (id, { navigate: doNavigate = true } = {}) => {
    const mc = availableMedicalCenters.find(m => m.id === id);
    if (!mc) return;
    setSelected(mc);
    if (doNavigate) {
      navigate(`/medical_centers/${id}/patients`);
    }
  };

  const value = useMemo(() => ({
    medicalCenterId: selected?.id || null,
    selectedMedicalCenter: selected,
    availableMedicalCenters,
    selectMedicalCenter,
  }), [selected, availableMedicalCenters]);

  return (
    <MedicalCenterContext.Provider value={value}>
      {children}
    </MedicalCenterContext.Provider>
  );
};
export { useMedicalCenter };